/**
 * FinanceRejections — the rejected invoice-approval log.
 *
 * Reads GET /v1/approvals?status=rejected (the ListApprovals SQL filters on
 * ($2 = '' OR status = $2); the HTTP layer only defaults an EMPTY status to
 * pending). Then, per row, GET /v1/runs/{runId} for errorMessage — which IS
 * the rejection reason: DecideApproval writes the reviewer's justification to
 * runs.error_message via CommandTransitionRunStatus. N+1, but N is the
 * rejection count.
 *
 * Rows rejected before the justification field shipped return the server's
 * default, "approval rejected by human reviewer". That is HONEST — no reason
 * was given. It is not a placeholder and must not be hidden or prettified.
 *
 * Invoice gates only, selected CLIENT-SIDE by payload shape via the shared
 * asExtractedInvoicePayload guard — the same idiom FinanceApprovals uses.
 *
 * No raw hex — ink-* tailwind classes only.
 */
import { useState, useEffect } from 'react'
import { SectionTitle, LoadingState, ErrorState, EmptyState } from '../../DashboardShared'
import { usePolledAsync, fetchRejectedApprovals, fetchRejectionReason } from '../../api'
import type { DashApproval } from '../../types'
import { asExtractedInvoicePayload } from '../../../../features/review/extractedInvoice'
import type { ExtractedInvoice } from '../../../../features/review/extractedInvoice'

// The server's fallback when a rejection carried no justification. Matching it
// lets a row say "No reason recorded" rather than parrot a sentence that only
// means the field was empty.
const NO_REASON_GIVEN = 'approval rejected by human reviewer'

function money(n: number, currency = 'GBP'): string {
  const sym = currency === 'GBP' ? '£' : currency === 'USD' ? '$' : currency === 'EUR' ? '€' : ''
  return `${sym}${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function fmtDateTime(d?: string | null): string {
  return d
    ? new Date(d).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
    : '—'
}

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many
}

interface RejectedInvoice {
  approval: DashApproval
  invoice: ExtractedInvoice
}

export default function FinanceRejections() {
  const { data, loading, error } = usePolledAsync(fetchRejectedApprovals, 30_000, [])
  const [reasons, setReasons] = useState<Record<string, string | null>>({})

  // The reason is on the run, not the approval — one lookup per row. A failed
  // lookup leaves the row blank rather than inventing a reason.
  useEffect(() => {
    if (!data) return
    let cancelled = false
    void (async () => {
      const found: Record<string, string | null> = {}
      for (const a of data) {
        if (!a.runId || !asExtractedInvoicePayload(a.payload)) continue
        try {
          found[a.runId] = await fetchRejectionReason(a.runId)
        } catch {
          found[a.runId] = null
        }
      }
      if (!cancelled) setReasons(found)
    })()
    return () => { cancelled = true }
  }, [data])

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} />

  const items: RejectedInvoice[] = (data ?? []).flatMap(a => {
    const invoice = asExtractedInvoicePayload(a.payload)
    if (!invoice) return []
    return [{ approval: a, invoice }]
  })

  return (
    <div className="page-enter flex flex-col gap-4 mx-auto" style={{ maxWidth: 1040, minWidth: 920 }}>
      <SectionTitle
        title="Rejections"
        subtitle={`Finance · ${items.length} rejected invoice ${plural(items.length, 'approval', 'approvals')}`}
      />

      <div className="k-card">
        {items.length === 0 ? (
          <EmptyState
            message="No rejections"
            hint="Invoices you reject in Approvals appear here with the justification you gave."
          />
        ) : (
          <div>
            <div className="finance-thead flex items-center gap-4 px-5 py-2 border-b border-edge-row">
              <span className="w-32 flex-shrink-0">Invoice</span>
              <span className="w-48 flex-shrink-0">Vendor</span>
              <span className="w-24 flex-shrink-0 text-right">Amount</span>
              <span className="flex-1 min-w-0">Reason</span>
              <span className="w-[130px] flex-shrink-0 text-right">Requested</span>
            </div>
            {items.map(item => {
              const raw = reasons[item.approval.runId]
              const pending = !(item.approval.runId in reasons)
              const given = raw != null && raw !== NO_REASON_GIVEN
              return (
                <div key={item.approval.id}
                  className="flex items-center gap-4 px-5 py-3 border-b border-edge-row-faint last:border-b-0">
                  <span className="w-32 flex-shrink-0 text-12 font-medium text-ink-secondary tabular-nums truncate">{item.invoice.invoiceNumber || '—'}</span>
                  <span className="w-48 flex-shrink-0 text-14 font-medium text-ink-primary truncate">{item.invoice.vendorName}</span>
                  <span className="w-24 flex-shrink-0 text-right text-14 font-medium text-ink-primary tabular-nums">{money(item.invoice.amount ?? 0, item.invoice.currency)}</span>
                  <span className={`flex-1 min-w-0 text-13 truncate ${given ? 'text-ink-body' : 'text-ink-faint italic'}`} title={raw ?? undefined}>
                    {pending ? '…' : given ? raw : 'No reason recorded'}
                  </span>
                  <span className="w-[130px] flex-shrink-0 text-right text-12 text-ink-muted tabular-nums">{fmtDateTime(item.approval.requestedAt)}</span>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
